import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Autocomplete from '../Autocomplete';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { payments as paymentsApi } from '@afosto/order/api/Payments';
import { profile as profileApi } from '@afosto/iam/api/Profile';

const PAYMENT_METHODS_PATH = 'afosto.tenant.odr.providers.v1.methods';

const PaymentMethodSearch = ({ clientId, ...props }) => {
  const intl = useIntl();
  const [paymentMethodSuggestions, setPaymentMethodSuggestions] = useState([]);
  const [isFetchingPaymentMethods, setIsFetchingPaymentMethods] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchMethods = async () => {
      try {
        setIsFetchingPaymentMethods(true);

        const response = await paymentsApi.listPaymentMethods({
          headers: {
            'x-page-size': 100,
          },
        });
        const { data = [] } = response;
        let methods = data;

        if (clientId) {
          const profileResponse = await profileApi.getProfile(
            PAYMENT_METHODS_PATH.replace('tenant', clientId),
          );
          const { data: profileMethods = {} } = profileResponse ?? {};
          const profileMethodCodes = (profileMethods?.methods ?? [])
            .filter(method => method.is_enabled)
            .map(method => method.code);

          methods = methods.filter(method => profileMethodCodes.includes(method.code));
        }

        const formattedMethods = methods.map(method => ({
          ...method,
          label: intl.formatMessage({
            id: `paymentMethods.${method?.provider_code?.toLowerCase()}.${method.code}`,
            defaultMessage: method.name,
            description: `label of the payment method for code ${method.code}`,
          }),
          providerLabel: intl.formatMessage({
            id: `paymentMethods.providers.${method.provider_code.toLowerCase()}`,
            defaultMessage: method.provider_code,
            description: `label of the payment method provider for code ${method.provider_code}`,
          }),
          value: method.code,
        }));

        if (isMounted) {
          setPaymentMethodSuggestions(sortArrayByProperty(formattedMethods, 'providerLabel'));
        }
      } catch (err) {
        if (isMounted) {
          setPaymentMethodSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPaymentMethods(false);
        }
      }
    };

    fetchMethods();

    return () => {
      isMounted = false;
    };
  }, [intl, clientId]);

  return (
    <Autocomplete
      {...props}
      options={paymentMethodSuggestions}
      loading={isFetchingPaymentMethods}
      autocomplete="off"
      groupBy={option => option.providerLabel}
    />
  );
};

PaymentMethodSearch.propTypes = {
  ...Autocomplete.propTypes,
  clientId: PropTypes.string,
};

PaymentMethodSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  clientId: undefined,
};

export default PaymentMethodSearch;
